import mitt, { Emitter } from 'mitt';
import { LabPartnerStreamMessage, LabPartnerStreamState } from './types';
import { debugService } from '../debugService';

interface StreamStartEvent { sessionId: string; requestId: string; stream: LabPartnerStreamState }
interface StreamUpdateEvent { sessionId: string; requestId: string; stream: LabPartnerStreamState; delta: string }
interface StreamProgressEvent { sessionId: string; requestId: string; stream: LabPartnerStreamState; progress: { stepId: string; label: string; status: string; order: number; percentage?: number; stepType?: string; details?: unknown; error?: string } }
interface StreamCompleteEvent {
  sessionId: string; requestId: string; stream: LabPartnerStreamState;
  finalData: {
    text: string;
    modelActionsContent?: string;
    modelActions?: Array<{ content?: string; action: string }>;
    featureActionsContent?: string;
    featureActions?: Array<{ content?: string; action: string; feature?: { featureId: string; name: string | null; description: string | null; formula: string | null } }>;
    userId: string;
    timestamp: string;
  };
}
interface StreamErrorEvent {
  sessionId: string; requestId: string; stream?: LabPartnerStreamState;
  error: {
    code: string;
    message: string;
    errorId?: string;
    retryable?: boolean;
    userId: string;
    timestamp: string;
  };
}

type StreamEvents = {
  streamStart: StreamStartEvent;
  streamUpdate: StreamUpdateEvent;
  streamProgress: StreamProgressEvent;
  streamComplete: StreamCompleteEvent;
  streamError: StreamErrorEvent;
};

/**
 * Processes lab partner stream messages, handling sequence ordering and event emission
 */
class StreamProcessor {
  private static instance: StreamProcessor;
  private emitter: Emitter<StreamEvents>;
  private activeStreams: Map<string, LabPartnerStreamState> = new Map();

  private constructor() {
    this.emitter = mitt<StreamEvents>();
  }

  static getInstance(): StreamProcessor {
    if (!StreamProcessor.instance) {
      StreamProcessor.instance = new StreamProcessor();
    }
    return StreamProcessor.instance;
  }

  /**
   * Process an incoming lab partner stream message
   */
  processStreamMessage(message: LabPartnerStreamMessage) {
    const requestId = message.requestId;
    const sessionId = message.sessionId;

    switch (message.type) {
      case 'text_delta':
        this.handleTextDelta(sessionId, requestId, message);
        break;

      case 'model_action':
        this.handleModelAction(sessionId, requestId, message);
        break;

      case 'feature_action':
        this.handleFeatureAction(sessionId, requestId, message);
        break;

      case 'progress':
        this.handleProgress(sessionId, requestId, message);
        break;

      case 'done':
        this.handleDone(sessionId, requestId, message);
        break;

      case 'error':
        this.handleError(sessionId, requestId, message);
        break;

      default:
        // Unknown message type, ignore silently
    }
  }

  private handleTextDelta(sessionId: string, requestId: string, message: LabPartnerStreamMessage) {
    let stream = this.activeStreams.get(requestId);

    const isFirstTextDelta = !stream || stream.accumulatedText === '';

    if (!stream) {
      // Create new stream state with sequence tracking
      stream = {
        requestId,
        sessionId,
        accumulatedText: '',
        startTime: Date.now(),
        lastUpdate: Date.now(),
        isComplete: false,
        nextExpectedSequence: undefined,
        messageBuffer: new Map(),
        lastProcessedSequence: undefined,
        pendingFrame: null
      };
      this.activeStreams.set(requestId, stream);
    }

    // If this is the first text_delta (even if progress created the stream), emit streamStart
    if (isFirstTextDelta) {
      // Record first delta timing
      debugService.recordFirstDelta(requestId);

      this.emitter.emit('streamStart', {
        sessionId,
        requestId,
        stream
      });
    }

    // Check if message has a sequence number
    if (message.sequence !== undefined) {
      // Initialize sequence tracking if this is the first sequenced message
      if (stream.nextExpectedSequence === undefined) {
        stream.nextExpectedSequence = message.sequence;
        stream.lastProcessedSequence = message.sequence - 1;
      }

      // Check if this is the expected sequence
      if (message.sequence === stream.nextExpectedSequence) {
        // Process this message immediately
        this.processTextDelta(stream, message, sessionId, requestId);

        // Check if we can process any buffered messages
        this.flushBuffer(stream, sessionId, requestId);
      } else if (message.sequence > stream.nextExpectedSequence!) {
        // This message is out of order - buffer it
        stream.messageBuffer!.set(message.sequence, message);

        // Set a timeout to flush buffer if we don't get missing messages
        if (!stream.bufferTimeout) {
          stream.bufferTimeout = setTimeout(() => {
            this.forceFlushBuffer(stream, sessionId, requestId);
          }, 500); // Wait 500ms for missing sequences
        }
      }
      // Ignore messages with sequence < nextExpectedSequence (duplicates or late arrivals)
    } else {
      // No sequence number - process immediately (backward compatibility)
      this.processTextDelta(stream, message, sessionId, requestId);
    }
  }

  private processTextDelta(stream: LabPartnerStreamState, message: LabPartnerStreamMessage, sessionId: string, requestId: string) {
    // Type guard to ensure we have text_delta
    if (message.type !== 'text_delta') {
      return;
    }

    // Record delta timing (for tracking gaps between deltas)
    debugService.recordDelta(requestId);

    // Accumulate text
    stream.accumulatedText += message.data.content;
    stream.lastUpdate = Date.now();

    // Update sequence tracking if applicable
    if (message.sequence !== undefined) {
      stream.lastProcessedSequence = message.sequence;
      stream.nextExpectedSequence = message.sequence + 1;
    }

    // Throttle UI updates to once per animation frame
    if (!stream.pendingFrame) {
      stream.pendingFrame = requestAnimationFrame(() => {
        stream.pendingFrame = null;
        this.emitter.emit('streamUpdate', {
          sessionId,
          requestId,
          stream,
          delta: ''
        });
      });
    }
  }

  private flushBuffer(stream: LabPartnerStreamState, sessionId: string, requestId: string) {
    if (!stream.messageBuffer || stream.messageBuffer.size === 0) return;

    // Process messages in sequence order while we have consecutive sequences
    while (stream.messageBuffer.has(stream.nextExpectedSequence!)) {
      const bufferedMessage = stream.messageBuffer.get(stream.nextExpectedSequence!);
      if (!bufferedMessage) break;
      stream.messageBuffer.delete(stream.nextExpectedSequence!);

      this.processTextDelta(stream, bufferedMessage, sessionId, requestId);
    }

    // Clear timeout if buffer is empty
    if (stream.messageBuffer.size === 0 && stream.bufferTimeout) {
      clearTimeout(stream.bufferTimeout);
      stream.bufferTimeout = undefined;
    }
  }

  private forceFlushBuffer(stream: LabPartnerStreamState, sessionId: string, requestId: string) {
    if (!stream.messageBuffer || stream.messageBuffer.size === 0) return;

    // Sort buffered messages by sequence and process them
    const sortedSequences = Array.from(stream.messageBuffer.keys()).sort((a, b) => a - b);

    for (const seq of sortedSequences) {
      const bufferedMessage = stream.messageBuffer.get(seq)!;
      this.processTextDelta(stream, bufferedMessage, sessionId, requestId);
    }

    // Clear the buffer and timeout
    stream.messageBuffer.clear();
    if (stream.bufferTimeout) {
      clearTimeout(stream.bufferTimeout);
      stream.bufferTimeout = undefined;
    }
  }

  private handleModelAction(sessionId: string, requestId: string, message: LabPartnerStreamMessage) {
    // Model actions during streaming - stored in stream state
    let stream = this.activeStreams.get(requestId);
    if (!stream) {
      stream = this.createNewStream(sessionId, requestId);
      this.activeStreams.set(requestId, stream);
    }

    if (message.type === 'model_action' && message.data.actions) {
      if (!stream.modelActions) {
        stream.modelActions = [];
      }
      // Store model actions for processing at completion
      stream.modelActions.push(...message.data.actions);
    }
  }

  private handleFeatureAction(sessionId: string, requestId: string, message: LabPartnerStreamMessage) {
    // Feature actions during streaming - stored in stream state
    let stream = this.activeStreams.get(requestId);
    if (!stream) {
      stream = this.createNewStream(sessionId, requestId);
      this.activeStreams.set(requestId, stream);
    }

    if (message.type === 'feature_action' && message.data.actions) {
      if (!stream.featureActions) {
        stream.featureActions = [];
      }
      // Store feature actions for processing at completion
      stream.featureActions.push(...message.data.actions);
    }
  }

  private handleProgress(sessionId: string, requestId: string, message: LabPartnerStreamMessage) {
    if (message.type !== 'progress') return;

    let stream = this.activeStreams.get(requestId);

    if (!stream) {
      stream = this.createNewStream(sessionId, requestId);
      stream.currentProgressLabel = message.data.label;
      this.activeStreams.set(requestId, stream);
    } else {
      stream.currentProgressLabel = message.data.label;
      stream.lastUpdate = Date.now();
    }

    // Emit progress event
    this.emitter.emit('streamProgress', {
      sessionId,
      requestId,
      stream,
      progress: message.data
    });
  }

  private handleDone(sessionId: string, requestId: string, message: LabPartnerStreamMessage) {
    const stream = this.activeStreams.get(requestId);

    if (!stream) {
      console.warn('Received done for unknown stream:', requestId);
      return;
    }

    // Record done timing
    debugService.recordDone(requestId);

    this.finishStream(sessionId, requestId, stream, message);
  }

  private finishStream(sessionId: string, requestId: string, stream: LabPartnerStreamState, message: LabPartnerStreamMessage) {
    // Force flush any remaining buffered messages before finishing
    if (stream.messageBuffer && stream.messageBuffer.size > 0) {
      this.forceFlushBuffer(stream, sessionId, requestId);
    }

    // Cancel any pending rAF frame
    if (stream.pendingFrame) {
      cancelAnimationFrame(stream.pendingFrame);
      stream.pendingFrame = null;
    }

    // Clear any pending buffer timeout
    if (stream.bufferTimeout) {
      clearTimeout(stream.bufferTimeout);
      stream.bufferTimeout = undefined;
    }

    // Mark stream as complete
    stream.isComplete = true;
    stream.lastUpdate = Date.now();

    // Extract data from 'done' message
    let modelActions: Array<{ content?: string; action: string }> = [];
    let featureActions: Array<{ content?: string; action: string; feature?: { featureId: string; name: string | null; description: string | null; formula: string | null } }> = [];
    let modelActionsContent: string | undefined = undefined;
    let featureActionsContent: string | undefined = undefined;

    if (message.type === 'done') {
      modelActionsContent = message.data.modelActionsContent;
      featureActionsContent = message.data.featureActionsContent;

      if (message.data.modelActions) {
        modelActions = message.data.modelActions;
      }

      if (message.data.featureActions) {
        featureActions = message.data.featureActions;
      }
    }

    // Emit complete event with the final message data
    this.emitter.emit('streamComplete', {
      sessionId,
      requestId,
      stream,
      finalData: {
        text: stream.accumulatedText,
        modelActionsContent,
        modelActions,
        featureActionsContent,
        featureActions,
        userId: message.userId,
        timestamp: message.timestamp
      }
    });

    // Clean up stream after a delay
    setTimeout(() => {
      // Final cleanup - ensure pending frame and timeout are cleared
      const streamToClean = this.activeStreams.get(requestId);
      if (streamToClean?.pendingFrame) {
        cancelAnimationFrame(streamToClean.pendingFrame);
      }
      if (streamToClean?.bufferTimeout) {
        clearTimeout(streamToClean.bufferTimeout);
      }
      this.activeStreams.delete(requestId);
    }, 5000);
  }

  private handleError(sessionId: string, requestId: string, message: LabPartnerStreamMessage) {
    if (message.type !== 'error') return;

    const stream = this.activeStreams.get(requestId);

    // Emit error event
    this.emitter.emit('streamError', {
      sessionId,
      requestId,
      stream,
      error: {
        code: message.data.code || message.data.errorType || 'unknown',
        message: message.data.message,
        errorId: message.data.errorId,
        retryable: message.data.retryable,
        userId: message.userId,
        timestamp: message.timestamp
      }
    });

    // Clean up stream
    if (stream) {
      if (stream.pendingFrame) {
        cancelAnimationFrame(stream.pendingFrame);
      }
      if (stream.bufferTimeout) {
        clearTimeout(stream.bufferTimeout);
      }
      this.activeStreams.delete(requestId);
    }
  }

  private createNewStream(sessionId: string, requestId: string): LabPartnerStreamState {
    return {
      requestId,
      sessionId,
      accumulatedText: '',
      startTime: Date.now(),
      lastUpdate: Date.now(),
      isComplete: false,
      nextExpectedSequence: undefined,
      messageBuffer: new Map(),
      lastProcessedSequence: undefined,
      pendingFrame: null
    };
  }

  getActiveStream(requestId: string): LabPartnerStreamState | undefined {
    return this.activeStreams.get(requestId);
  }

  getActiveStreamsForSession(sessionId: string): LabPartnerStreamState[] {
    const streams: LabPartnerStreamState[] = [];
    for (const stream of this.activeStreams.values()) {
      if (stream.sessionId === sessionId) {
        streams.push(stream);
      }
    }
    return streams;
  }

  hasActiveStreams(sessionId: string): boolean {
    for (const stream of this.activeStreams.values()) {
      if (stream.sessionId === sessionId && !stream.isComplete) {
        return true;
      }
    }
    return false;
  }

  clearStreamsForSession(sessionId: string) {
    const toDelete: string[] = [];
    for (const [requestId, stream] of this.activeStreams.entries()) {
      if (stream.sessionId === sessionId) {
        if (stream.pendingFrame) {
          cancelAnimationFrame(stream.pendingFrame);
        }
        if (stream.bufferTimeout) {
          clearTimeout(stream.bufferTimeout);
        }
        toDelete.push(requestId);
      }
    }
    toDelete.forEach(id => this.activeStreams.delete(id));
  }

  clearAllStreams() {
    // Clear any pending frames and buffer timeouts before clearing all streams
    for (const stream of this.activeStreams.values()) {
      if (stream.pendingFrame) {
        cancelAnimationFrame(stream.pendingFrame);
      }
      if (stream.bufferTimeout) {
        clearTimeout(stream.bufferTimeout);
      }
    }
    this.activeStreams.clear();
  }

  // Event emitter methods
  on<K extends keyof StreamEvents>(event: K, handler: (data: StreamEvents[K]) => void) {
    this.emitter.on(event, handler);
  }

  off<K extends keyof StreamEvents>(event: K, handler: (data: StreamEvents[K]) => void) {
    this.emitter.off(event, handler);
  }
}

export const streamProcessor = StreamProcessor.getInstance();